import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Plus, Pencil, Trash2, X, Check, Folder, 
  FileSignature, Receipt, Mail, BarChart, ClipboardList, 
  FileText, Users, Scale, Megaphone, Tag
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const ICON_MAP = {
  FileSignature, Receipt, Mail, BarChart, ClipboardList, 
  FileText, Users, Scale, Megaphone, Folder, Tag
};

const COLOR_OPTIONS = [
  { value: 'blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  { value: 'green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  { value: 'purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  { value: 'orange', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  { value: 'teal', bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' },
  { value: 'pink', bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
  { value: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  { value: 'red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  { value: 'amber', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  { value: 'slate', bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
];

const getColorClasses = (color) => {
  return COLOR_OPTIONS.find(c => c.value === color) || COLOR_OPTIONS[9];
};

const CategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Folder',
    color: 'slate'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: editingId ? prev.slug : generateSlug(value)
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '', icon: 'Folder', color: 'slate' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingId 
        ? `${API}/categories/${editingId}`
        : `${API}/categories`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save category');
      }

      toast.success(editingId ? 'Category updated!' : 'Category created!');
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || 'Folder',
      color: category.color || 'slate'
    });
    setEditingId(category.category_id);
    setShowForm(true);
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!confirm(`Delete "${categoryName}"? Templates using it will be uncategorized.`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete category');
      }

      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const IconComponent = ({ iconName, className }) => {
    const Icon = ICON_MAP[iconName] || Folder;
    return <Icon className={className} />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/templates')}
              className="rounded-xl"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Templates
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Categories</h1>
            <p className="text-slate-500 mt-1">Organize your templates with categories</p>
          </div>
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="rounded-xl bg-violet-600 hover:bg-violet-700"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="mb-8 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? 'Edit Category' : 'New Category'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Category Name *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g., Client Proposals"
                    className="h-11 rounded-xl"
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Slug *
                  </Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                    placeholder="client-proposals"
                    className="h-11 rounded-xl font-mono text-sm"
                    data-testid="category-slug-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Used for filtering and identification</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Description
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description for this category..."
                  rows={2}
                  className="rounded-xl resize-none"
                  data-testid="category-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Icon</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(ICON_MAP).map(iconName => {
                      const Icon = ICON_MAP[iconName];
                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, icon: iconName }))}
                          className={`p-2.5 rounded-lg border-2 transition-all ${
                            formData.icon === iconName
                              ? 'border-violet-500 bg-violet-50 text-violet-700'
                              : 'border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                          data-testid={`icon-${iconName}`}
                        >
                          <Icon className="w-5 h-5" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                        className={`w-9 h-9 rounded-lg border-2 transition-all ${color.bg} ${
                          formData.color === color.value
                            ? `${color.border} ring-2 ring-offset-1 ring-${color.value}-400`
                            : 'border-transparent hover:scale-110'
                        }`}
                        data-testid={`color-${color.value}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="rounded-xl"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="rounded-xl bg-violet-600 hover:bg-violet-700"
                  data-testid="save-category-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 rounded-2xl bg-white border border-dashed border-slate-300 text-center">
            <Folder className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No categories yet</p>
            <p className="text-sm text-slate-400 mt-1">Create one to organize your templates</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(category => {
              const colorClasses = getColorClasses(category.color);
              return (
                <div
                  key={category.category_id}
                  className="p-4 rounded-xl bg-white border border-slate-200 hover:shadow-md transition-shadow"
                  data-testid={`category-${category.slug}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2.5 rounded-lg ${colorClasses.bg}`}>
                      <IconComponent iconName={category.icon} className={`w-5 h-5 ${colorClasses.text}`} />
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700"
                        data-testid={`edit-${category.slug}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.category_id, category.name)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"
                        data-testid={`delete-${category.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900">{category.name}</h3>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">{category.slug}</p>
                  {category.description && (
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{category.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
